import { z } from 'zod';
import { BaseResponseSchema, } from './primitives.js';
import { GatewayOverallHealthSchema } from './ops-gateway-health.schemas.js';
// ========================================
// Provider Ops — EFEM Gateway Peripherals Health (Phase 0)
// ========================================
export const OpsDeviceHealthSchema = z.enum([
    'healthy',
    'degraded',
    'unhealthy',
    'unknown',
    'not_applicable',
]);
export const EfemPeripheralCategorySchema = z.enum([
    'barcode-scanner',
    'handheld-barcode-scanner',
    'waferid-camera',
    'efem-adapter',
]);
export const EfemPeripheralRuntimeStatusSchema = z.enum(['ready', 'degraded', 'unreachable']);
export const EfemPeripheralHealthRowSchema = z.object({
    gatewaySerialNumber: z.string(),
    tenantId: z.string().nullable().optional(),
    siteId: z.string().nullable().optional(),
    peripheralCategory: EfemPeripheralCategorySchema,
    peripheralSerialNumber: z.string().nullable().optional(),
    roleKey: z.string().nullable().optional(),
    roleLabel: z.string().nullable().optional(),
    configured: z.boolean(),
    bound: z.boolean(),
    inventoryStatus: z.string().nullable().optional(),
    runtimeStatus: EfemPeripheralRuntimeStatusSchema.nullable().optional(),
    runtimeLastSeenAt: z.string().nullable().optional(),
    runtimeDetail: z.string().nullable().optional(),
    gatewayOverallHealth: GatewayOverallHealthSchema,
    adapterReady: z.boolean().nullable().optional(),
    overallHealth: OpsDeviceHealthSchema,
    healthReason: z.string().nullable().optional(),
});
export const EfemPeripheralHealthSummarySchema = z.object({
    total: z.number().int().min(0),
    bound: z.number().int().min(0),
    healthy: z.number().int().min(0),
    degraded: z.number().int().min(0),
    unhealthy: z.number().int().min(0),
    unknown: z.number().int().min(0),
    notApplicable: z.number().int().min(0),
});
export const EfemPeripheralHealthListQuerySchema = z.object({
    tenantId: z.string().optional(),
    siteId: z.string().optional(),
    gatewaySerialNumber: z.string().optional(),
    peripheralCategory: EfemPeripheralCategorySchema.optional(),
    overallHealth: OpsDeviceHealthSchema.optional(),
    search: z.string().max(100).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const EfemPeripheralHealthListResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        peripherals: z.array(EfemPeripheralHealthRowSchema),
        summary: EfemPeripheralHealthSummarySchema,
        nextCursor: z.string().nullable().optional(),
        count: z.number().int().min(0),
    }),
});
/** Edge EFEM orchestrator heartbeat peripheral probe entry (serialized as peripheralsJson). */
export const EfemGatewayPeripheralHeartbeatEntrySchema = z
    .object({
    category: z.enum(['barcode-scanner', 'handheld-barcode-scanner', 'waferid-camera']),
    serialNumber: z.string().min(1).optional(),
    profileRef: z.string().min(1).optional(),
    identityMode: z.enum(['edge-guard', 'cloud-resolve']).optional(),
    status: EfemPeripheralRuntimeStatusSchema,
    lastProbeAt: z.string().datetime(),
    detail: z.string().nullable().optional(),
})
    .superRefine((val, ctx) => {
    if (!val.serialNumber && !val.profileRef) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'serialNumber or profileRef is required on peripheral heartbeat entry',
            path: ['serialNumber'],
        });
    }
});
export const EfemGatewayPeripheralHeartbeatListSchema = z.array(EfemGatewayPeripheralHeartbeatEntrySchema);
/** Parse peripheralsJson from CnapseIotDeviceRegistry heartbeat row. */
export function parseEfemGatewayPeripheralHeartbeatJson(json) {
    if (!json?.trim())
        return [];
    try {
        const parsed = JSON.parse(json);
        return EfemGatewayPeripheralHeartbeatListSchema.parse(parsed);
    }
    catch {
        return [];
    }
}
export const ProbePeripheralsResultSchema = z.object({
    peripherals: z.array(EfemGatewayPeripheralHeartbeatEntrySchema),
    probedAt: z.string().datetime(),
});
export const OpsPeripheralProbeResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        serialNumber: z.string(),
        requestId: z.string(),
        probedAt: z.string().datetime(),
        peripherals: z.array(EfemGatewayPeripheralHeartbeatEntrySchema),
    }),
});
//# sourceMappingURL=ops-efem-peripherals-health.schemas.js.map